import type { Filter, QueryOrder } from "@cubejs-client/core";
import type { CloudCostType } from "app/contexts/UserContext";
import type { ActionBarSortBy } from "app/views/dashboards/utils/costDashboardActionBar";
import type { FilterBarCubeFilters } from "app/views/dashboards/utils/costDashboardFilterBar";

/**
 * Default cost type to use when no cost type is specified
 */
export const DEFAULT_COST_TYPE: CloudCostType = "BilledCost";

/**
 * Standard mapping for account-related fields to Account cube (joined dimension)
 */
export const ACCOUNT_FIELD_MAPPING: Record<string, string> = {
  // CSO cube mappings
  "CSO.account_name": "Account.name",
  "CSO.account_id": "Account.key",
  "CSO.account_email": "Account.email",
  "CSO.account_groups": "Account.groups",
  // CSOTag cube mappings
  "CSOTag.account_name": "Account.name",
  "CSOTag.account_id": "Account.key",
  "CSOTag.account_email": "Account.email",
  "CSOTag.account_groups": "Account.groups",
};

/**
 * Checks if filters contain any Account-related filters
 *
 * @param filters - The filters to check
 * @returns True if any filters reference Account dimensions
 */
export function hasAccountFilters(filters?: FilterBarCubeFilters): boolean {
  if (!filters) return false;

  const checkFilter = (filter: Filter): boolean => {
    if ("and" in filter && filter.and) {
      return filter.and.some(checkFilter);
    }
    if ("or" in filter && filter.or) {
      return filter.or.some(checkFilter);
    }
    if ("member" in filter && typeof filter.member === "string") {
      // Check if member starts with "Account." (direct Account cube reference)
      if (filter.member.startsWith("Account.")) {
        return true;
      }
      // Check if member is a known account field from our mapping
      // This prevents false positives from fields like "Tags.my_account_name"
      return filter.member in ACCOUNT_FIELD_MAPPING;
    }
    return false;
  };

  return filters.some((filterGroup) => {
    if ("or" in filterGroup) {
      return filterGroup.or.some(checkFilter);
    }
    if ("and" in filterGroup) {
      return filterGroup.and.some(checkFilter);
    }
    return false;
  });
}

/**
 * Checks if filters contain specific unsupported filter members
 *
 * @param filters - The filters to check
 * @param unsupportedMembers - Array of member names that are not supported
 * @returns True if any filters reference unsupported members
 */
export function hasUnsupportedFilters(
  filters: FilterBarCubeFilters | undefined,
  unsupportedMembers: string[],
): boolean {
  if (!filters) return false;

  const checkFilter = (filter: Filter): boolean => {
    if ("and" in filter && filter.and) {
      return filter.and.some(checkFilter);
    }
    if ("or" in filter && filter.or) {
      return filter.or.some(checkFilter);
    }
    if ("member" in filter && typeof filter.member === "string") {
      return unsupportedMembers.includes(filter.member);
    }
    return false;
  };

  return filters.some((filterGroup) => {
    if ("or" in filterGroup) {
      return filterGroup.or.some(checkFilter);
    }
    if ("and" in filterGroup) {
      return filterGroup.and.some(checkFilter);
    }
    return false;
  });
}

/**
 * Get the cost field name based on cost type
 *
 * @param costType - The cost type (e.g., "ListCost", "BilledCost", "EffectiveCost")
 * @param prefix - The cube name prefix (e.g., "CSO", "CSOTag")
 * @returns The appropriate cost field name
 *
 * @example
 * ```tt diffs
 * getCostFieldName("BilledCost", "CSO") // "CSO.monthlyBilledSavingOpportunity"
 * getCostFieldName("ListCost", "CSOTag") // "CSOTag.monthlyListSavingOpportunity"
 * getCostFieldName(undefined, "CSO") // "CSO.monthlyBilledSavingOpportunity" (uses DEFAULT_COST_TYPE)
 * ```
 */
export function getCostFieldName(
  costType: CloudCostType | undefined,
  prefix: string,
): string {
  const effectiveCostType = costType ?? DEFAULT_COST_TYPE;
  switch (effectiveCostType) {
    case "ListCost":
      return `${prefix}.monthlyListSavingOpportunity`;
    case "BilledCost":
      return `${prefix}.monthlyBilledSavingOpportunity`;
    case "EffectiveCost":
      return `${prefix}.monthlyEffectiveSavingOpportunity`;
    default:
      return `${prefix}.monthlyBilledSavingOpportunity`;
  }
}

/**
 * Transforms ActionBarSortBy (from buildSortBy) to CSO cube format
 *
 * Maps legacy CostOpportunity/CostOpportunityTag sort fields to CSO/CSOTag fields
 * based on cost type configuration.
 *
 * @param sortBy - The sort configuration from buildSortBy
 * @param costType - The cost type configuration (e.g., "BilledCost")
 * @param isByTag - Whether to use CSOTag (true) or CSO (false) cube
 * @returns The transformed order object for Cube.js query, or undefined if no sortBy
 *
 * @remarks
 * **TEMPORARY:** This function exists to support migration from legacy CostOpportunity cubes.
 * Once buildSortBy is updated to generate CSO cube member names, this transformation can be removed.
 *
 * @example
 * ```ts
 * // Input: ["CostOpportunity.monthlySavingOpportunity", "desc"]
 * // Output: { "CSO.monthlyBilledSavingOpportunity": "desc" } (if costType is "BilledCost")
 * transformSortByToCSO(sortBy, "BilledCost", false)
 * ```
 */
export function transformSortByToCSO(
  sortBy: ActionBarSortBy | undefined,
  costType: CloudCostType | undefined,
  isByTag: boolean,
): Record<string, QueryOrder> | undefined {
  if (!sortBy) return undefined;

  const [member, direction] = sortBy;
  const prefix = isByTag ? "CSOTag" : "CSO";

  // Transform monthlySavingOpportunity to appropriate cost type field
  if (member.includes("monthlySavingOpportunity")) {
    const costField = getCostFieldName(costType, prefix);
    return { [costField]: direction };
  }

  // Transform nonCompliantResourceCount
  if (member.includes("nonCompliantResourceCount")) {
    return { [`${prefix}.nonCompliantResourceCount`]: direction };
  }

  return undefined;
}
